"use client"

import { createPortal } from "react-dom"
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"

import SharePosterInner from "./SharePosterInner"

import "./styles.scss"

import { SharePosterInnerProps, SharePosterProps, SharePosterRef } from "./types"

const SharePoster = forwardRef<SharePosterRef, SharePosterProps>(({ open, ...props }, ref) => {
  const [mounted, setMounted] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const innerRef = useRef<SharePosterRef>(null)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  const download = () => {
    if (open) {
      // If already open, just trigger download on the inner component
      innerRef.current?.download()
    } else {
      // If closed, mount temporarily in silent mode to download
      setIsDownloading(true)
    }
  }

  useImperativeHandle(ref, () => ({
    download,
  }))

  const handleDownloadComplete = () => {
    setIsDownloading(false)
  }

  // Only render if mounted (client-side) AND (open OR downloading)
  if (!mounted || (!open && !isDownloading)) return null

  // Determine props for the inner component
  const innerProps: SharePosterInnerProps = {
    ...props,
    open: open || isDownloading, // Force open if downloading
    silent: props.silent || isDownloading, // Force silent if downloading (unless already silent)
    autoDownload: isDownloading, // Trigger auto download if in downloading state
    onDownloadComplete: handleDownloadComplete,
  }

  return createPortal(<SharePosterInner ref={innerRef} {...innerProps} />, document.body)
})

SharePoster.displayName = "SharePoster"

export default SharePoster
export * from "./types"
